import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Layout from '../../components/Layout';
import Card from '../../ui/Card';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import Button from '../../ui/Button';
import Tabs from '../../ui/Tabs/Tabs';
import { Field, Input, Select } from '../../ui/Field/Field';
import LoadingSpinner from '../../components/LoadingSpinner';
import { kpiService } from '../../services/kpiService';
import logger from '../../utils/logger';
import { openedIn, branchMatch, periodBounds, dayKey, missingSets, opsSinceFor, KPI_TAB_DATA, REVENUE_BRANCH_CODE, REVENUE_CITIES } from './kpi.logic';
import { USE_CONTRACTS_V2 } from '../../services/contractsV2';
import { Notice } from '../OperationsPortal/components/collectionsShared';
import { PRESETS, periodOf } from '../BranchCharges/branchCharges.logic';
import { Segmented } from '../BranchCharges/BranchCharges';

// Tabs
import KPISettings from './tabs/KPISettings';
import OperationsDashboard from './tabs/OperationsDashboard';
import EmployeeKPIs from './tabs/EmployeeKPIs';
import FleetKPIs from './tabs/FleetKPIs_v2';
import ContractsKPIs from './tabs/ContractsKPIs';
import RevenueKPIs from './tabs/RevenueKPIs';
import OverviewKPIs from './tabs/OverviewKPIs';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';

const TABS = [
    // its own permission since permissions phase 4 (4 Oct 2026; it followed the Operations tab until the upgrade)
    { id: 'overview', label: 'Overview', icon: ICONS.dashboard, permission: 'view_kpi_overview' },
    { id: 'ops', label: 'Operations', icon: ICONS.operations, permission: PERMISSIONS.view_kpi_ops },
    { id: 'employees', label: 'Employees', icon: ICONS.staff, permission: PERMISSIONS.view_kpi_employees },
    { id: 'fleet', label: 'Fleet', icon: ICONS.fleet, permission: PERMISSIONS.view_kpi_fleet },
    { id: 'contracts', label: 'Contracts', icon: ICONS.contracts, permission: PERMISSIONS.view_kpi_contracts },
    { id: 'financial', label: 'Revenue', icon: ICONS.wallet, permission: PERMISSIONS.view_kpi_financial },
    { id: 'settings', label: 'Settings', icon: ICONS.settings, permission: PERMISSIONS.manage_kpi_settings },
];

const KPIModule = () => {
    const { hasPermission } = usePermissions();

    const allowedTabs = useMemo(() => {
        return TABS.filter(tab => !tab.permission || hasPermission(tab.permission));
    }, [hasPermission]);

    const [activeTab, setActiveTab] = useState(allowedTabs[0]?.id || 'ops');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ ops: [], fleet: [], staffMappings: {}, excluded: [] });
    const [loaded, setLoaded] = useState({});          // data set -> read (a tab reads only what it shows)
    const [loadError, setLoadError] = useState('');
    const stale = useRef(new Set());                   // sets to read past the caches (after Sync)
    const pending = useRef(new Set());                 // sets being read now (never read twice at once)

    // Global Filters: a period preset (this month by default, local dates) or two dates
    const [preset, setPreset] = useState('month');
    const [filters, setFilters] = useState(() => {
        const p = periodOf('month');
        return { startDate: p.from, endDate: p.to, city: 'All', branch: 'All' };
    });
    const pickPreset = (id) => {
        setPreset(id);
        if (id !== 'custom') {
            const p = periodOf(id);
            setFilters((f) => ({ ...f, startDate: p.from, endDate: p.to }));
        }
    };

    // The contracts store (USE_CONTRACTS_V2) serves the contracts open now or returned since a day, not five years:
    // the ops are read from the first day the chosen period needs, and read again when a period needs an earlier one.
    const opsSince = useMemo(() => (USE_CONTRACTS_V2 ? opsSinceFor(filters.startDate, filters.endDate) : undefined),
        [filters.startDate, filters.endDate]);
    const opsSinceRef = useRef(opsSince);
    const opsLoadedSince = useRef(undefined);
    useEffect(() => {
        opsSinceRef.current = opsSince;
        if (USE_CONTRACTS_V2 && loaded.ops && opsSince && opsLoadedSince.current && opsSince < opsLoadedSince.current) {
            setLoaded((l) => ({ ...l, ops: false }));
        }
    }, [opsSince, loaded.ops]);

    // Each tab reads only the Firestore sets it shows (the Revenue tab none: it is the engine's report), each
    // once; a failed read is said on the page instead of an empty list shown as "0" (audit MON-20).
    const fetchSets = useCallback(async (wanted) => {
        const sets = wanted.filter((k) => !pending.current.has(k));
        if (!sets.length) return;
        sets.forEach((k) => pending.current.add(k));
        const LOADERS = {
            ops: (force) => {
                const since = opsSinceRef.current;
                return kpiService.getUnifiedOpsView(force, { since }).then((ops) => { opsLoadedSince.current = since; return ops; });
            },
            fleet: (force) => kpiService.getUnifiedFleetView(force),
            staffMappings: () => kpiService.getStaffMappings(),
            excluded: () => kpiService.getStaffExcluded(),
        };
        setLoading(true);
        setLoadError('');
        try {
            const values = await Promise.all(sets.map((k) => LOADERS[k](stale.current.has(k))));
            sets.forEach((k) => stale.current.delete(k));
            setData((d) => ({ ...d, ...Object.fromEntries(sets.map((k, i) => [k, values[i]])) }));
            setLoaded((l) => ({ ...l, ...Object.fromEntries(sets.map((k) => [k, true])) }));
        } catch (error) {
            logger.error('Error fetching KPI data:', error);
            setLoadError(error?.message || String(error));
        } finally {
            sets.forEach((k) => pending.current.delete(k));
            setLoading(pending.current.size > 0);
        }
    }, []);

    useEffect(() => {
        if (!loadError) fetchSets(missingSets(activeTab, loaded));
    }, [activeTab, loaded, loadError, fetchSets]);

    // Sync: every set is read again past the caches - this tab's now, the others when opened
    const fetchData = useCallback(() => {
        stale.current = new Set(Object.values(KPI_TAB_DATA).flat());
        setLoadError('');
        setLoaded({});
    }, []);
    const retry = () => setLoadError('');
    const blocked = !!loadError && missingSets(activeTab, loaded).length > 0;

    const inCity = useCallback((item) => filters.city === 'All' || item.city === filters.city, [filters.city]);

    // Contracts that ran during the period (for revenue): picked up by its end, not returned before its start
    const filteredOps = useMemo(() => {
        const { s, e } = periodBounds(filters.startDate, filters.endDate);
        const now = Date.now();
        return data.ops.filter((item) => {
            if (item.pickupAt == null || item.operationStatus === 'Cancelled') return false;
            const end = item.dropoffAt ?? (item.operationStatus === 'In-Progress' ? now : item.pickupAt);
            return item.pickupAt <= e && end >= s && branchMatch(item.branch, filters.branch) && inCity(item);
        });
    }, [data.ops, filters.startDate, filters.endDate, filters.branch, inCity]);

    // Contracts opened in the period (performance)
    const activityOps = useMemo(
        () => openedIn(data.ops, { from: filters.startDate, to: filters.endDate, branch: filters.branch }).filter(inCity),
        [data.ops, filters.startDate, filters.endDate, filters.branch, inCity],
    );

    // Employees and operations read the whole history (previous period, cars on the road), in the city
    const cityOps = useMemo(() => data.ops.filter(inCity), [data.ops, inCity]);

    // until the contracts are read (the Revenue tab never needs them) the choices are the report's own
    const cities = useMemo(() => (loaded.ops ? ['All', ...new Set(data.ops.map(i => i.city))].filter(Boolean) : REVENUE_CITIES), [data.ops, loaded.ops]);
    const branches = useMemo(() => {
        if (!loaded.ops) return Object.keys(REVENUE_BRANCH_CODE);
        const filtered = filters.city === 'All' ? data.ops : data.ops.filter(i => i.city === filters.city);
        const distinctBranches = [...new Set(filtered.map(i => i.branch))]
            .filter(Boolean)
            .filter(b => b !== 'Unassigned');

        const order = ['Terminal 1', 'Terminal 2', 'Business Bay', 'Invygo'];
        const known = order.filter((b) => distinctBranches.includes(b));
        const others = distinctBranches.filter((b) => !order.includes(b)).sort();
        const airport = known.includes('Terminal 1') || known.includes('Terminal 2') ? ['Airport'] : [];
        return ['All', ...airport, ...known, ...others];
    }, [data.ops, filters.city, loaded.ops]);

    const renderTabContent = () => {
        switch (activeTab) {
            case 'overview': return <OverviewKPIs filters={filters} />;     // the engine's answer (one Overview)
            case 'ops': return <OperationsDashboard allOps={cityOps} filters={filters} />;
            case 'employees': return <EmployeeKPIs allOps={cityOps} filters={filters} excluded={data.excluded} />;
            case 'fleet': return <FleetKPIs data={data.fleet} allOps={data.ops} filters={filters} />;
            case 'contracts': return <ContractsKPIs data={activityOps} filters={filters} />;
            case 'financial': return <RevenueKPIs filters={filters} />;
            case 'settings': return <KPISettings onSave={() => fetchData(true)} staffMappingsProp={data.staffMappings} />;
            default: return null;
        }
    };

    const showAllAvailable = () => {
        const times = data.ops.map((o) => o.pickupAt).filter((t) => t != null);
        if (times.length > 0) {
            setPreset('custom');
            setFilters({ ...filters, startDate: dayKey(Math.min(...times)), endDate: dayKey(Math.max(...times)) });
        }
    };

    const syncAction = hasPermission(PERMISSIONS.sync_kpi_data) ? (
        <Button
            variant="primary"
            onClick={() => fetchData(true)}
            disabled={loading}
            leadingIcon={<Icon glyph={ICONS.refresh} size="sm" className={loading ? 'animate-spin' : ''} />}
        >
            {loading ? 'Syncing...' : 'Sync'}
        </Button>
    ) : null;

    return (
        <Layout title="KPI Dashboard" subtitle="Performance Metrics & Analytics" actions={syncAction}>
            <div className="flex flex-col gap-6 w-full">

                {/* Global Filter Bar */}
                {/* solid, not the frosted card background: it stays on top of the content while scrolling */}
                <Card padding="sm" className="sticky top-0 z-30" style={{ background: 'var(--surface)' }}>
                    <div className="flex flex-wrap gap-4 items-end">
                        <fieldset className="min-w-[200px]">
                            <legend className="ds-field__label mb-1">Period</legend>
                            <div className="flex flex-wrap items-center gap-2">
                                <Segmented value={preset} onChange={pickPreset} options={PRESETS.map((p) => [p.id, p.label])} label="Period" />
                                {preset === 'custom' ? (
                                    <>
                                        <Input
                                            type="date"
                                            aria-label="Start date"
                                            value={filters.startDate}
                                            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                                        />
                                        <Icon glyph={ICONS.arrowRight} size="sm" tone="muted" />
                                        <Input
                                            type="date"
                                            aria-label="End date"
                                            value={filters.endDate}
                                            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                                        />
                                    </>
                                ) : (
                                    <span className="text-xs text-gray-500 tabular-nums">
                                        {filters.startDate === filters.endDate ? filters.startDate : `${filters.startDate} to ${filters.endDate}`}
                                    </span>
                                )}
                            </div>
                        </fieldset>

                        <Field label="City" className="min-w-[150px]">
                            <Select
                                value={filters.city}
                                onChange={(e) => setFilters({...filters, city: e.target.value, branch: 'All'})}
                                options={cities}
                            />
                        </Field>

                        <Field label="Branch" className="min-w-[150px]">
                            <Select
                                value={filters.branch}
                                onChange={(e) => setFilters({...filters, branch: e.target.value})}
                                options={branches}
                            />
                        </Field>
                    </div>
                </Card>

                {data.ops.length > 0 && filteredOps.length === 0 && (
                    <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg flex items-center gap-3" role="status">
                        <Icon glyph={ICONS.warning} size="md" tone="warning" />
                        <div>
                            <p className="text-sm font-semibold text-amber-800">No data for the selected range.</p>
                            <p className="text-xs text-amber-700">We found {data.ops.length} total contracts, but none match these filters. Try changing the dates.</p>
                        </div>
                        <Button variant="link" size="sm" className="ml-auto" onClick={showAllAvailable}>
                            Show All Available
                        </Button>
                    </div>
                )}

                {/* Tab Navigation */}
                <Tabs
                    items={allowedTabs.map(tab => ({ id: tab.id, label: tab.label, icon: <Icon glyph={tab.icon} size="sm" /> }))}
                    value={activeTab}
                    onChange={(id) => { setLoadError(''); setActiveTab(id); }}
                    label="KPI sections"
                    idPrefix="kpi-tab"
                />

                {/* Content */}
                <div
                    className="min-h-[600px] overflow-visible"
                    role="tabpanel"
                    id={`kpi-tab-panel-${activeTab}`}
                    aria-labelledby={`kpi-tab-${activeTab}`}
                >
                    {blocked ? (
                        <div className="flex flex-col items-start gap-3">
                            <Notice tone="danger">
                                The KPI data could not be read: {String(loadError).replace(/\.+$/, '')}. Nothing is shown rather than zeros - try again.
                            </Notice>
                            <Button size="sm" onClick={retry} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>Try again</Button>
                        </div>
                    ) : loading || missingSets(activeTab, loaded).length > 0 ? (
                        <LoadingSpinner message="Aggregating live performance data..." />
                    ) : (
                        renderTabContent()
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default KPIModule;
